/**
 * Behavior form-effect — configures confirmation, redirect, auto-save,
 * progress bar, and multiple-submission settings via body attributes.
 * SSR-safe: returns undefined if document is not available.
 */

import type { FormEffectHandler } from '@atlas-forms/types-js';
import { str } from './utils';

export const behaviorEffect: FormEffectHandler = (config) => {
  if (typeof document === 'undefined') return;

  let autoSaveTimer: ReturnType<typeof setInterval> | undefined;

  // Confirmation type
  const confirmationType = str(config.confirmationType) || 'message';
  document.body.setAttribute('data-atlas-confirmation-type', confirmationType);

  // Confirmation message
  const confirmationMessage = str(config.confirmationMessage);
  if (confirmationMessage) {
    document.body.setAttribute('data-atlas-confirmation-message', confirmationMessage);
  }

  // Redirect URL
  const redirectUrl = str(config.redirectUrl);
  if (redirectUrl) {
    document.body.setAttribute('data-atlas-redirect-url', redirectUrl);
  }

  // Redirect delay (only set if non-zero)
  const redirectDelaySeconds = Number(config.redirectDelaySeconds ?? 0);
  if (redirectDelaySeconds) {
    document.body.setAttribute('data-atlas-redirect-delay', String(redirectDelaySeconds));
  }

  // Progress bar
  if (config.showProgressBar === true) {
    document.body.setAttribute('data-atlas-progress-bar', 'true');
  }

  // Single-submission mode
  if (config.allowMultipleSubmissions === false) {
    document.body.setAttribute('data-atlas-single-submission', 'true');
  }

  // Auto-save: dispatch custom event every 30 seconds
  if (config.autoSaveEnabled === true) {
    autoSaveTimer = setInterval(() => {
      document.dispatchEvent(new CustomEvent('atlas-forms:auto-save'));
    }, 30_000);
  }

  return () => {
    if (autoSaveTimer !== undefined) {
      clearInterval(autoSaveTimer);
    }
    document.body.removeAttribute('data-atlas-confirmation-type');
    document.body.removeAttribute('data-atlas-confirmation-message');
    document.body.removeAttribute('data-atlas-redirect-url');
    document.body.removeAttribute('data-atlas-redirect-delay');
    document.body.removeAttribute('data-atlas-progress-bar');
    document.body.removeAttribute('data-atlas-single-submission');
  };
};
